import React, { useState, useEffect } from 'react';
import { SiteData } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { getSiteDisplayAddress } from '../../utils/security';
import {
  exportSiteToZip,
  getExportPreviewSource,
  getSiteExportSummary,
  ExportSourcePreview,
} from '../../services/exportEngine';
import {
  Download,
  FileCode,
  Check,
  Copy,
  X,
  Sparkles,
  Layers,
  Image,
  Globe,
  ShieldCheck,
  AlertCircle,
  FileText,
  Code,
  Loader2,
  ExternalLink,
} from 'lucide-react';

interface WebsiteExportModalProps {
  site: SiteData;
  isOpen: boolean;
  onClose: () => void;
}

export const WebsiteExportModal: React.FC<WebsiteExportModalProps> = ({
  site,
  isOpen,
  onClose,
}) => {
  const { isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'summary' | 'html' | 'css' | 'js'>('summary');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [previewData, setPreviewData] = useState<ExportSourcePreview | null>(null);

  // Options
  const [includeImages, setIncludeImages] = useState(true);
  const [includeAnimations, setIncludeAnimations] = useState(true);
  const [includeSeo, setIncludeSeo] = useState(true);
  const [includeSitemap, setIncludeSitemap] = useState(true);

  useEffect(() => {
    if (isOpen && site && isSuperAdmin) {
      try {
        const preview = getExportPreviewSource(site);
        setPreviewData(preview);
      } catch (err) {
        console.error('Failed to generate export preview:', err);
      }
    }
  }, [isOpen, site, isSuperAdmin]);

  if (!isOpen || !isSuperAdmin) return null;

  const summary = previewData?.summary || getSiteExportSummary(site);

  const handleDownloadZip = async () => {
    setIsExporting(true);
    try {
      const blob = await exportSiteToZip(site, {
        includeImages,
        includeAnimations,
        includeSeo,
        includeSitemap,
      });

      // Browser download trigger
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${site.subdomain || 'adiosoft'}-website-export.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('ZIP export failed:', err);
      setExportError(err?.message || 'Dışa aktarma sırasında bir hata oluştu.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyCode = (text: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
      <div className="bg-[#0B1324] border border-[#263750] rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden text-slate-200 font-sans">
        {/* Top Header */}
        <div className="px-6 py-4 bg-[#111B2E] border-b border-[#263750] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 flex items-center justify-center">
              <FileCode className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <span>HTML / CSS / JS Dışa Aktarım</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600/20 text-blue-300 font-mono border border-blue-500/30">
                  Export Engine 2.0
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                {site.name} ({getSiteDisplayAddress(site)})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadZip}
              disabled={isExporting}
              className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-blue-600/30 transition disabled:opacity-50 active:scale-95"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>ZIP Hazırlanıyor...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>ZIP Olarak İndir</span>
                </>
              )}
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-[#263750] bg-[#0E1729] px-6 gap-2 shrink-0">
          {[
            { id: 'summary', label: 'Özet & Ayarlar', icon: Layers },
            { id: 'html', label: 'index.html', icon: Code },
            { id: 'css', label: 'styles.css', icon: FileCode },
            { id: 'js', label: 'main.js', icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-3 px-3.5 text-xs font-bold flex items-center gap-2 border-b-2 transition ${
                  isActive
                    ? 'border-blue-500 text-blue-400 bg-blue-500/5'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Body */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-[#0B1324]">
          {activeTab === 'summary' && (
            <div className="space-y-6">
              {/* Metrics Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-4 bg-[#111B2E] border border-[#263750] rounded-xl text-center">
                  <div className="text-2xl font-black text-white">{summary.pagesCount}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-semibold">HTML Sayfası</div>
                </div>

                <div className="p-4 bg-[#111B2E] border border-[#263750] rounded-xl text-center">
                  <div className="text-2xl font-black text-blue-400">{summary.sectionsCount}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-semibold">Tasarım Bölümü</div>
                </div>

                <div className="p-4 bg-[#111B2E] border border-[#263750] rounded-xl text-center">
                  <div className="text-2xl font-black text-purple-400">{summary.animationsCount}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-semibold">Aktif Animasyon</div>
                </div>

                <div className="p-4 bg-[#111B2E] border border-[#263750] rounded-xl text-center">
                  <div className="text-2xl font-black text-emerald-400">{summary.imagesCount}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-semibold">Görsel / Asset</div>
                </div>
              </div>

              {/* Dynamic Features Detected */}
              <div className="p-4 bg-[#111B2E] border border-[#263750] rounded-xl space-y-2">
                <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-400" />
                  <span>Dinamik Özellikler & Fonksiyonlar</span>
                </h3>
                <div className="flex flex-wrap gap-2 pt-1">
                  {summary.dynamicFeatures.length > 0 ? (
                    summary.dynamicFeatures.map((f, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-300 border border-blue-500/20 text-xs font-semibold"
                      >
                        ✓ {f}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">Temel statik içerik blokları</span>
                  )}
                </div>
              </div>

              {/* Export Scope Options */}
              <div className="p-4 bg-[#111B2E] border border-[#263750] rounded-xl space-y-3">
                <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider">
                  Dışa Aktarma Paket Ayarları
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <label className="flex items-center gap-2.5 p-2 bg-[#0B1324] rounded-lg border border-[#263750] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeImages}
                      onChange={(e) => setIncludeImages(e.target.checked)}
                      className="rounded text-blue-600 bg-slate-800 border-slate-600"
                    />
                    <span>Görselleri ZIP içerisine paketle (assets/images)</span>
                  </label>

                  <label className="flex items-center gap-2.5 p-2 bg-[#0B1324] rounded-lg border border-[#263750] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeAnimations}
                      onChange={(e) => setIncludeAnimations(e.target.checked)}
                      className="rounded text-blue-600 bg-slate-800 border-slate-600"
                    />
                    <span>Animasyon motorunu ve keyframe'leri ekle</span>
                  </label>

                  <label className="flex items-center gap-2.5 p-2 bg-[#0B1324] rounded-lg border border-[#263750] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeSeo}
                      onChange={(e) => setIncludeSeo(e.target.checked)}
                      className="rounded text-blue-600 bg-slate-800 border-slate-600"
                    />
                    <span>SEO meta etiketleri ve Schema.org JSON-LD</span>
                  </label>

                  <label className="flex items-center gap-2.5 p-2 bg-[#0B1324] rounded-lg border border-[#263750] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeSitemap}
                      onChange={(e) => setIncludeSitemap(e.target.checked)}
                      className="rounded text-blue-600 bg-slate-800 border-slate-600"
                    />
                    <span>sitemap.xml ve robots.txt dosyalarını üret</span>
                  </label>
                </div>
              </div>

              {/* Informative Notice */}
              <div className="p-3.5 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-300 text-xs flex items-start gap-2.5 leading-relaxed">
                <ShieldCheck className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">Bağımsız Statik Mimari:</span> Üretilen dosyalar herhangi bir Node.js veya veritabanı gerektirmeksizin doğrudan Apache, Nginx, Vercel, Netlify veya GitHub Pages üzerinde çalışır. Platform atıfı standart olarak sayfa altında korunur.
                </div>
              </div>
            </div>
          )}

          {activeTab === 'html' && previewData && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-mono text-slate-400">index.html (Önizleme)</span>
                <button
                  onClick={() => handleCopyCode(previewData.html)}
                  className="px-3 py-1.5 rounded-lg bg-[#1E293B] hover:bg-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 transition"
                >
                  {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedCode ? 'Kopyalandı' : 'Kodu Kopyala'}</span>
                </button>
              </div>
              <pre className="p-4 bg-[#060A14] border border-[#263750] rounded-xl text-xs font-mono text-slate-300 overflow-x-auto max-h-[500px] leading-relaxed custom-scrollbar">
                <code>{previewData.html}</code>
              </pre>
            </div>
          )}

          {activeTab === 'css' && previewData && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-mono text-slate-400">css/styles.css (Önizleme)</span>
                <button
                  onClick={() => handleCopyCode(previewData.css)}
                  className="px-3 py-1.5 rounded-lg bg-[#1E293B] hover:bg-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 transition"
                >
                  {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedCode ? 'Kopyalandı' : 'Kodu Kopyala'}</span>
                </button>
              </div>
              <pre className="p-4 bg-[#060A14] border border-[#263750] rounded-xl text-xs font-mono text-slate-300 overflow-x-auto max-h-[500px] leading-relaxed custom-scrollbar">
                <code>{previewData.css}</code>
              </pre>
            </div>
          )}

          {activeTab === 'js' && previewData && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-mono text-slate-400">js/main.js (Önizleme)</span>
                <button
                  onClick={() => handleCopyCode(previewData.js)}
                  className="px-3 py-1.5 rounded-lg bg-[#1E293B] hover:bg-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 transition"
                >
                  {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedCode ? 'Kopyalandı' : 'Kodu Kopyala'}</span>
                </button>
              </div>
              <pre className="p-4 bg-[#060A14] border border-[#263750] rounded-xl text-xs font-mono text-slate-300 overflow-x-auto max-h-[500px] leading-relaxed custom-scrollbar">
                <code>{previewData.js}</code>
              </pre>
            </div>
          )}
        </div>

        {/* Modal Bottom Footer */}
        <div className="px-6 py-3.5 bg-[#111B2E] border-t border-[#263750] flex items-center justify-between shrink-0">
          <div className="text-xs text-slate-400">
            Tüm HTML, CSS, JavaScript dosyaları ve görseller tek bir ZIP paketinde sunulur.
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-[#1E293B] hover:bg-slate-700 text-slate-300 text-xs font-bold transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};
